/**
 * Production-Ready Outdated Agents Check Manager
 *
 * Features:
 * - Periodic check: every 1 hour to check for outdated agents
 * - Tab coordination: BroadcastChannel to sync across tabs
 * - Heartbeat backup: checks every 5 minutes if setTimeout fails
 * - Visibility handling: immediate check when tab becomes visible (if interval elapsed)
 * - Single-flight pattern: only one check at a time
 * - Login trigger: checks immediately on login
 */

import { getAgents, type Agent } from '@/services/api';
import { isAgentOutdated } from './versionUtils';

// Constants
const CHECK_CHANNEL = 'outdated-agents-check';
const CHECK_LOCK_KEY = 'outdated-agents-check-lock';
const LAST_CHECK_KEY = 'outdated-agents-last-check';
// Ten seconds max lock duration
const LOCK_TIMEOUT = 10000;
// One hour
const CHECK_INTERVAL = 60 * 60 * 1000;
// Five minutes - backup check mechanism
const HEARTBEAT_INTERVAL = 5 * 60 * 1000;

interface CheckMessage {
  type: 'check-started' | 'check-completed' | 'check-failed';
  timestamp: number;
  outdatedCount?: number;
}

export type OutdatedAgentsCallback = (count: number) => void;

// Get the current agent version from environment
function getCurrentAgentVersion(): string {
  return (import.meta as unknown as { env?: Record<string, unknown> }).env?.VITE_AGENT_VERSION as string || '0.0.19';
}

class OutdatedAgentsCheckManager {
  private checkTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private checkPromise: Promise<void> | null = null;
  private callback: OutdatedAgentsCallback | null = null;
  private channel: BroadcastChannel | null = null;
  private isPaused = false;
  private lastCheckAttempt = 0;
  private isInitialized = false;

  constructor() {
    // Initialize BroadcastChannel if supported
    if (typeof BroadcastChannel !== 'undefined') {
      try {
        this.channel = new BroadcastChannel(CHECK_CHANNEL);
        this.channel.onmessage = (event: MessageEvent<CheckMessage>) => {
          this.handleChannelMessage(event.data);
        };
      } catch (error) {
        console.warn('[OutdatedAgentsCheck] BroadcastChannel not available:', error);
      }
    }

    // Listen for visibility changes
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        this.handleVisibilityChange();
      });
    }
  }

  /**
   * Handle messages from other tabs
   */
  private handleChannelMessage(message: CheckMessage): void {
    if (message.type === 'check-completed' && message.outdatedCount !== undefined) {
      // Update our local state with the count from another tab
      if (this.callback) {
        this.callback(message.outdatedCount);
      }
      // Update last check time
      this.updateLastCheckTime();
    }
  }

  /**
   * Handle visibility changes - check when tab becomes visible if needed
   */
  private handleVisibilityChange(): void {
    if (typeof document === 'undefined') return;

    const isHidden = document.hidden;

    if (isHidden && !this.isPaused) {
      this.isPaused = true;
    } else if (!isHidden && this.isPaused) {
      this.isPaused = false;

      // Check if we need to run a check (if interval has elapsed)
      if (this.shouldCheck()) {
        console.log('[OutdatedAgentsCheck] Tab visible and interval elapsed, checking now');
        void this.executeCheck();
      }
    }
  }

  /**
   * Check if we should run a check based on last check time
   */
  private shouldCheck(): boolean {
    if (!this.isInitialized) return false;

    const lastCheck = this.getLastCheckTime();
    if (!lastCheck) return true;

    return Date.now() - lastCheck >= CHECK_INTERVAL;
  }

  /**
   * Get last check time from localStorage
   */
  private getLastCheckTime(): number | null {
    if (typeof localStorage === 'undefined') return null;

    try {
      const stored = localStorage.getItem(LAST_CHECK_KEY);
      return stored ? parseInt(stored, 10) : null;
    } catch {
      return null;
    }
  }

  /**
   * Update last check time in localStorage
   */
  private updateLastCheckTime(): void {
    if (typeof localStorage === 'undefined') return;

    try {
      localStorage.setItem(LAST_CHECK_KEY, Date.now().toString());
    } catch (error) {
      console.warn('[OutdatedAgentsCheck] Failed to update last check time:', error);
    }
  }

  /**
   * Heartbeat mechanism - runs every 5 minutes as backup to setTimeout
   */
  private heartbeat(): void {
    if (!this.isInitialized || this.isPaused) {
      return;
    }

    if (this.shouldCheck()) {
      console.log('[OutdatedAgentsCheck] Heartbeat detected need for check');
      void this.executeCheck();
    }
  }

  /**
   * Start heartbeat timer
   */
  private startHeartbeat(): void {
    if (this.heartbeatTimer) return;

    this.heartbeatTimer = setInterval(() => {
      this.heartbeat();
    }, HEARTBEAT_INTERVAL);
  }

  /**
   * Stop heartbeat timer
   */
  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  /**
   * Try to acquire a lock for check (localStorage-based for cross-tab)
   */
  private tryAcquireLock(): boolean {
    if (typeof localStorage === 'undefined') return true;

    try {
      const lockData = localStorage.getItem(CHECK_LOCK_KEY);
      if (lockData) {
        const lock = JSON.parse(lockData) as { timestamp: number };
        const lockAge = Date.now() - lock.timestamp;

        if (lockAge < LOCK_TIMEOUT) {
          // Lock held by another tab
          return false;
        }
      }

      // Acquire lock
      localStorage.setItem(CHECK_LOCK_KEY, JSON.stringify({ timestamp: Date.now() }));
      return true;
    } catch (error) {
      console.warn('[OutdatedAgentsCheck] Lock acquisition failed:', error);
      // Proceed if localStorage unavailable
      return true;
    }
  }

  /**
   * Release the check lock
   */
  private releaseLock(): void {
    if (typeof localStorage === 'undefined') return;

    try {
      localStorage.removeItem(CHECK_LOCK_KEY);
    } catch (error) {
      console.warn('[OutdatedAgentsCheck] Lock release failed:', error);
    }
  }

  /**
   * Broadcast message to other tabs
   */
  private broadcast(type: CheckMessage['type'], outdatedCount?: number): void {
    if (this.channel) {
      try {
        // eslint-disable-next-line unicorn/require-post-message-target-origin -- BroadcastChannel doesn't use targetOrigin
        this.channel.postMessage({ type, timestamp: Date.now(), outdatedCount } as CheckMessage);
      } catch (error) {
        console.warn('[OutdatedAgentsCheck] Broadcast failed:', error);
      }
    }
  }

  /**
   * Execute check with single-flight pattern
   */
  async executeCheck(): Promise<void> {
    // Rate limiting: prevent check spam (max once per 30 seconds)
    const now = Date.now();
    if (now - this.lastCheckAttempt < 30000) {
      console.log('[OutdatedAgentsCheck] Rate limited, skipping check');
      return;
    }
    this.lastCheckAttempt = now;

    // Single-flight: if already checking, return existing promise
    if (this.checkPromise) {
      console.log('[OutdatedAgentsCheck] Check already in progress, waiting...');
      return this.checkPromise;
    }

    // Try to acquire lock
    if (!this.tryAcquireLock()) {
      console.log('[OutdatedAgentsCheck] Another tab is checking, waiting...');
      return;
    }

    // Broadcast that we're starting check
    this.broadcast('check-started');

    // Create and store the promise
    this.checkPromise = (async () => {
      try {
        console.log('[OutdatedAgentsCheck] Starting outdated agents check...');

        // Fetch all agents with pagination (API has max limit of 100)
        let page = 1;
        // API max limit is 100
        const limit = 100;

        // Fetch first page
        let { data: response } = await getAgents({ query: { page, limit }, throwOnError: true });
        const allAgents: Agent[] = response.data || [];

        // Fetch remaining pages if there are more
        const totalPages = response.pagination?.total_pages || 1;
        while (page < totalPages) {
          page++;
          ({ data: response } = await getAgents({ query: { page, limit }, throwOnError: true }));
          if (response.data && response.data.length > 0) {
            allAgents.push(...response.data);
          }
        }

        const currentVersion = getCurrentAgentVersion();

        // Count outdated agents
        const outdatedCount = allAgents.filter(agent =>
          agent.agent_version && isAgentOutdated(agent.agent_version, currentVersion)
        ).length;

        console.log(`[OutdatedAgentsCheck] Check complete: ${outdatedCount} outdated agents found`);

        // Update callback
        if (this.callback) {
          this.callback(outdatedCount);
        }

        // Update last check time
        this.updateLastCheckTime();

        // Broadcast completion to other tabs
        this.broadcast('check-completed', outdatedCount);

        // Schedule next check
        this.scheduleNextCheck();
      } catch (error) {
        console.error('[OutdatedAgentsCheck] Check failed:', error);
        this.broadcast('check-failed');

        // Still schedule next check even on failure
        this.scheduleNextCheck();
      } finally {
        this.releaseLock();
        this.checkPromise = null;
      }
    })();

    return this.checkPromise;
  }

  /**
   * Schedule the next check
   */
  private scheduleNextCheck(): void {
    // Clear any existing timer
    if (this.checkTimer) {
      clearTimeout(this.checkTimer);
      this.checkTimer = null;
    }

    // Don't schedule if paused
    if (this.isPaused) {
      console.log('[OutdatedAgentsCheck] Tab hidden, will check on visibility');
      return;
    }

    console.log(`[OutdatedAgentsCheck] Next check scheduled in ${Math.floor(CHECK_INTERVAL / 60000)} minutes`);

    this.checkTimer = setTimeout(() => {
      console.log('[OutdatedAgentsCheck] Scheduled check triggered');
      void this.executeCheck();
    }, CHECK_INTERVAL);
  }

  /**
   * Initialize the manager and start periodic checks
   * Call this after login
   */
  start(callback: OutdatedAgentsCallback): void {
    console.log('[OutdatedAgentsCheck] Starting outdated agents check manager');

    this.callback = callback;
    this.isInitialized = true;

    // Start heartbeat
    this.startHeartbeat();

    // Execute immediate check
    void this.executeCheck();
  }

  /**
   * Stop the manager
   * Call this on logout
   */
  stop(): void {
    console.log('[OutdatedAgentsCheck] Stopping outdated agents check manager');

    this.isInitialized = false;
    this.callback = null;

    // Clear timers
    if (this.checkTimer) {
      clearTimeout(this.checkTimer);
      this.checkTimer = null;
    }
    this.stopHeartbeat();

    // Release lock if held
    this.releaseLock();
  }

  /**
   * Force an immediate check
   * Useful for manual triggers
   */
  forceCheck(): void {
    if (!this.isInitialized) {
      console.warn('[OutdatedAgentsCheck] Manager not initialized');
      return;
    }

    // Reset rate limit to allow immediate check
    this.lastCheckAttempt = 0;
    void this.executeCheck();
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    this.stop();
    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }
  }
}

// Export singleton instance
export const outdatedAgentsCheckManager = new OutdatedAgentsCheckManager();
